import { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import {
  Table,
  Button,
  Tag,
  Input,
  Card,
  Empty,
  Tooltip,
  Alert,
} from "antd";
import {
  SearchOutlined,
  SyncOutlined,
  TeamOutlined,
  PhoneOutlined,
  MailOutlined,
  ThunderboltOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import Breadcrumb from "../../components/Breadcrumb";
import { useToast } from "../../components/Toast/toastHook";
import {
  EmptyState,
  ErrorBanner,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import {
  GET_UNASSIGNED_BUYERS,
  GET_BUYER_POOL_COUNT,
  GET_FUB_SETTINGS,
  CLAIM_BUYER,
} from "../../graphql/fubSettingQueries";

const { Search } = Input;

const AgentBuyerPool = ({ embedded = false }) => {
  const { companyId } = useParams();
  const showToast = useToast();

  const [searchText, setSearchText] = useState("");
  const [claimingBuyerId, setClaimingBuyerId] = useState(null);

  // Check if FUB is connected
  const { data: fubData, loading: fubLoading } = useQuery(GET_FUB_SETTINGS, {
    variables: { companyId },
  });

  // Queries
  const { data, loading, error, refetch } = useQuery(GET_UNASSIGNED_BUYERS, {
    variables: { companyId },
    fetchPolicy: "network-only",
    skip: !fubData?.getFubSettings?.isConnected,
  });

  const { data: countData, refetch: refetchCount } = useQuery(GET_BUYER_POOL_COUNT, {
    variables: { companyId },
    fetchPolicy: "network-only",
    skip: !fubData?.getFubSettings?.isConnected,
  });

  // Mutations
  const [claimBuyer] = useMutation(CLAIM_BUYER, {
    onCompleted: (data) => {
      const buyer = data.claimBuyer;
      showToast(
        "success",
        `You claimed ${buyer.firstName} ${buyer.lastName}!`
      );
      setClaimingBuyerId(null);
      refetch();
      refetchCount();
    },
    onError: (error) => {
      setClaimingBuyerId(null);
      if (error.message.includes("already been claimed")) {
        showToast("warning", "This buyer was just claimed by another agent. Refreshing...");
        refetch();
        refetchCount();
      } else {
        showToast("error", error.message);
      }
    },
  });

  const isFubConnected = fubData?.getFubSettings?.isConnected;
  const buyers = data?.getUnassignedBuyers || [];
  const poolCount = countData?.getBuyerPoolCount || 0;

  // Filter buyers based on search
  const filteredBuyers = buyers.filter((buyer) => {
    if (!searchText) return true;
    const search = searchText.toLowerCase();
    return (
      buyer.firstName?.toLowerCase().includes(search) ||
      buyer.lastName?.toLowerCase().includes(search) ||
      buyer.email?.toLowerCase().includes(search) ||
      buyer.phone?.includes(search) ||
      buyer.leadSource?.toLowerCase().includes(search)
    );
  });

  const handleClaim = (buyer) => {
    setClaimingBuyerId(buyer._id);
    claimBuyer({
      variables: {
        buyerId: buyer._id,
      },
    });
  };

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <div>
          <span className="font-medium">
            {record.firstName} {record.lastName}
          </span>
          {record.fubId && (
            <Tag color="blue" className="ml-2" size="small">
              FUB
            </Tag>
          )}
        </div>
      ),
      sorter: (a, b) => (a.firstName || "").localeCompare(b.firstName || ""),
    },
    {
      title: "Contact",
      key: "contact",
      render: (_, record) => (
        <div className="space-y-1">
          {record.email && (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <MailOutlined />
              <a href={`mailto:${record.email}`}>{record.email}</a>
            </div>
          )}
          {record.phone && (
            <div className="flex items-center gap-1 text-sm text-gray-600">
              <PhoneOutlined />
              <a href={`tel:${record.phone}`}>{record.phone}</a>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Source",
      key: "source",
      dataIndex: "leadSource",
      render: (source) => source || <span className="text-gray-400">-</span>,
    },
    {
      title: "FUB Stage",
      key: "fubStage",
      dataIndex: "fubStage",
      render: (stage) =>
        stage ? (
          <Tag color="cyan">{stage}</Tag>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Tags",
      key: "tags",
      dataIndex: "tags",
      render: (tags) =>
        tags?.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {tags.slice(0, 3).map((tag, idx) => (
              <Tag key={idx} size="small">
                {tag}
              </Tag>
            ))}
            {tags.length > 3 && (
              <Tooltip title={tags.slice(3).join(", ")}>
                <Tag size="small">+{tags.length - 3}</Tag>
              </Tooltip>
            )}
          </div>
        ) : (
          <span className="text-gray-400">-</span>
        ),
    },
    {
      title: "Added",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (date) => (date ? new Date(date).toLocaleDateString() : "-"),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<ThunderboltOutlined />}
          onClick={() => handleClaim(record)}
          loading={claimingBuyerId === record._id}
          disabled={claimingBuyerId && claimingBuyerId !== record._id}
          style={{ backgroundColor: "#b4c540", borderColor: "#b4c540" }}
        >
          Claim
        </Button>
      ),
    },
  ];

  const firstLoad = (fubLoading || loading) && !filteredBuyers.length;

  // Show message if FUB is not connected
  if (!isFubConnected) {
    return (
      <div className={embedded ? "" : "m-4"}>
        {!embedded && (
          <Breadcrumb
            title="Buyer Pool"
            items={[
              { title: "Dashboard", href: `/company/${companyId}/agent/dashboard` },
              { title: "Buyer Pool" },
            ]}
          />
        )}
        <Card className={embedded ? "" : "mt-6"}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div className="text-center">
                <p className="text-gray-500 mb-2">
                  Buyer Pool is not available for your company.
                </p>
                <p className="text-gray-400 text-sm">
                  Contact your administrator to enable Follow Up Boss integration.
                </p>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div className={embedded ? "" : "m-4"}>
      {!embedded && (
        <Breadcrumb
          title="Buyer Pool"
          items={[
            { title: "Dashboard", href: `/company/${companyId}/agent/dashboard` },
            { title: "Buyer Pool" },
          ]}
        />
      )}

      {/* Info Alert */}
      <Alert
        message="Claim buyers to add them to your list"
        description={
          <span>
            These are unassigned buyers from Follow Up Boss. Click <strong>Claim</strong> to add a buyer to your list.
            <br />
            <LinkOutlined className="mr-1" />
            <em>Tip: Also assign yourself in Follow Up Boss to keep both systems aligned.</em>
          </span>
        }
        type="info"
        showIcon
        className="mt-6 mb-4"
      />

      {/* Stats Card */}
      <Card className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="bg-green-100 p-4 rounded-full">
              <TeamOutlined className="text-2xl text-green-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold">{poolCount}</h3>
              <p className="text-gray-500">Available Buyers</p>
            </div>
          </div>
          <Button
            icon={<SyncOutlined />}
            onClick={() => {
              refetch();
              refetchCount();
            }}
          >
            Refresh
          </Button>
        </div>
      </Card>

      {/* Search and Table */}
      <Card>
        <div className="mb-4">
          <Search
            placeholder="Search by name, email, phone, or source..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            onChange={(e) => setSearchText(e.target.value)}
            style={{ maxWidth: 400 }}
          />
        </div>

        {error && !filteredBuyers.length ? (
          <ErrorState onRetry={() => refetch()} />
        ) : (
          <>
            {error && <ErrorBanner onRetry={() => refetch()} />}
            <Table
              rowKey="_id"
              {...(firstLoad
                ? skeletonTableProps(columns)
                : {
                    columns,
                    dataSource: filteredBuyers,
                    pagination: {
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} buyers`,
                    },
                  })}
              locale={{
                emptyText: searchText ? (
                  <EmptyState
                    title="No buyers match your search"
                    description={`Nothing found for "${searchText}".`}
                    actionLabel="Clear search"
                    onAction={() => setSearchText("")}
                  />
                ) : (
                  <EmptyState
                    title="No unassigned buyers right now"
                    description="Buyers released to the pool will appear here to claim."
                  />
                ),
              }}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default AgentBuyerPool;
